import * as React from 'react'
import { useRef, useState } from 'react'
import isBetween from 'dayjs/plugin/isBetween'
import { usePartnersList } from './hooks'
import { CButton, CCardBody, CCol } from '@coreui/react-pro'
import { path } from 'src/libs'
import {
  Badge,
  Button,
  DatePicker,
  Divider,
  Input,
  InputRef,
  Space,
  Table,
  TimeRangePickerProps,
} from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import { FilterConfirmProps, FilterValue } from 'antd/es/table/interface'
import { Partner } from './types'
import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import CIcon from '@coreui/icons-react'
import { cilZoom } from '@coreui/icons'
import { Guard } from '../../../../features/session/guard'
import { useUnit } from 'effector-react'
import { $countriesByRegion } from 'src/features/dictionaries/model'

type DataIndex = keyof Partner

export const PartnersList = () => {
  const { partners, totalCountOfPartners, loading, onChangePartnersList } =
    usePartnersList()
  const countriesByRegion = useUnit($countriesByRegion)
  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const [orderBy, setOrderBy] = useState('')
  const [orderDirection, setOrderDirection] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  >(null)

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    currentSearchText,
    currentSearchedColumn,
    filters,
    currentDateRange,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    currentSearchText?: string
    currentSearchedColumn?: string
    filters?: Record<string, FilterValue | null>
    currentDateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  }) => {
    if (currentPage) {
      setPage(currentPage)
    } else {
      currentPage = page
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    } else {
      currentPageSize = pageSize
    }
    if (currentSearchText) {
      setSearchText(currentSearchText)
    } else {
      currentSearchText = searchText
    }
    if (currentSearchedColumn) {
      setSearchedColumn(currentSearchedColumn)
    } else {
      currentSearchedColumn = searchedColumn
    }
    if (currentOrderBy) {
      if (currentOrderBy == 'id') {
        currentOrderBy = 'id'
      }
      setOrderBy(currentOrderBy)
    } else {
      currentOrderBy = orderBy
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    } else {
      currentOrderDirection = orderDirection
    }
    if (filters) {
      setFiltersState(filters)
    }
    if (currentDateRange !== undefined) {
      setDateRange(currentDateRange)
    } else {
      currentDateRange = dateRange
    }

    onChangePartnersList({
      limit: currentPageSize,
      page: currentPage,
      orderBy: currentOrderBy,
      orderDirection: currentOrderDirection,
      searchText: currentSearchText,
      searchedColumn: currentSearchedColumn,
      filters: {
        status: filters
          ? filters?.status && (filters?.status as string[])
          : filtersState?.status && (filtersState.status as string[]),
        'client.region': filters
          ? filters?.['client.region'] && (filters['client.region'] as string[])
          : filtersState?.['client.region'] &&
            (filtersState['client.region'] as string[]),
        beginPeriodAt:
          currentDateRange && currentDateRange[0]
            ? currentDateRange[0].format('YYYY-MM-DD')
            : null,
        endPeriodAt:
          currentDateRange && currentDateRange[1]
            ? currentDateRange[1].format('YYYY-MM-DD')
            : null,
      },
    })
  }
  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: string,
  ) => {
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
    handleTableChange({
      currentSearchText: selectedKeys[0],
      currentSearchedColumn: dataIndex,
    })
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    setSearchText('')
  }

  const getColumnSearchProps = (
    dataIndex: string,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
  ]

  // TODO - доработать фильтрацию дат после доработки бэка
  const getColumnSearchDateProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <DatePicker.RangePicker
          format="YYYY-MM-DD"
          style={{ marginBottom: 8, display: 'block' }}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e ? [e] : [])}
          presets={rangePresets}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      dayjs.extend(isBetween)
      return record[dataIndex]
        ? dayjs(record[dataIndex]).isBetween(dayjs(value[0]), dayjs(value[1]))
        : false
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns: ColumnsType<Partner> = [
    {
      title: 'ID',
      ...getColumnSearchProps('id'),
      sorter: true,
      render: (item: Partner) => (
        <div>
          {item.client?.isTest ? (
            <Badge count={'Test'} offset={[0, -10]}>
              {item.id}
            </Badge>
          ) : (
            item.id
          )}
        </div>
      ),
    },
    {
      title: 'Key',
      dataIndex: 'key',
      ...getColumnSearchProps('key'),
      sorter: true,
    },
    {
      title: 'Client',
      children: [
        {
          title: 'E-mail',
          dataIndex: ['client', 'email'],
          ...getColumnSearchProps('client.email'),
          render: (item: string) => item,
          sorter: true,
        },
        {
          title: 'Full name',
          dataIndex: ['client', 'fullName'],
          ...getColumnSearchProps('client.fullName'),
          render: (item: string) => item,
        },
        {
          title: 'Country',
          dataIndex: ['client', 'country'],
          key: 'client.country',
          ...getColumnSearchProps('client.country'),
          render: (item: string) => item,
        },
        {
          title: 'Region',
          dataIndex: ['client', 'region'],
          key: 'client.region',
          filters: Object.keys(countriesByRegion).map((region) => ({
            text: region,
            value: region,
          })),
          render: (item: string) => item,
        },
      ],
    },
    {
      title: 'Registered at',
      dataIndex: 'registeredAt',
      ...getColumnSearchDateProps('registeredAt'),
      sorter: true,
    },
    {
      title: 'Partner status',
      dataIndex: 'status',
      filters: [
        { text: 'Bronze', value: 'bronze' },
        { text: 'Silver', value: 'silver' },
        { text: 'Gold', value: 'gold' },
        { text: 'Royal', value: 'royal' },
      ],
    },
    {
      title: 'All clients',
      dataIndex: 'partnerClientsCount',
      sorter: true,
    },
    {
      title: 'Active clients',
      dataIndex: 'activeClients',
      sorter: true,
    },
    {
      title: 'Lots',
      dataIndex: 'sumLots',
      ...getColumnSearchProps('sumLots'),
      sorter: true,
    },
    {
      title: 'Commissions',
      dataIndex: 'sumCommissions',
      sorter: true,
    },
    {
      title: 'Cashback',
      dataIndex: 'sumCashback',
      sorter: true,
    },
    {
      title: 'Deposits',
      dataIndex: 'sumDeposits',
      sorter: true,
    },
    {
      title: 'Withdrawals',
      dataIndex: 'sumWithdrawals',
      sorter: true,
    },
    {
      title: 'Actions',
      render: (item: Partner) => {
        return (
          <>
            <CButton
              color="secondary"
              className="me-2"
              title="View"
              href={path.ib.partners.details(item.id)}
            >
              <CIcon icon={cilZoom} />
            </CButton>
          </>
        )
      },
    },
  ]

  const csvContent = partners
    .map((item) => Object.values(item).join(','))
    .join('\n')

  const csvCode =
    'data:text/csv;charset=utf-8,SEP=,%0A' + encodeURIComponent(csvContent)

  return (
    <CCardBody>
      <CCol className=" d-flex align-items-baseline justify-content-start gap-2">
        <Guard permission="dowland_list_of_partners">
          <CButton
            color="primary"
            className="mb-2"
            href={csvCode}
            download="coreui-table-data.csv"
            target="_blank"
          >
            Download current items (.csv)
          </CButton>
        </Guard>
        <CButton
          color="info"
          className="mb-2"
          href={path.ib.partners.attach()}
          target="_blank"
        >
          Attach client to partner
        </CButton>
      </CCol>
      <Divider />
      <div style={{ marginBottom: 16 }}>
        <Space>
          <span>Period:</span>
          <DatePicker.RangePicker
            format="YYYY-MM-DD"
            value={dateRange}
            onChange={(dates) => {
              handleTableChange({ currentDateRange: dates })
            }}
            presets={rangePresets}
            placeholder={['Start Date', 'End Date']}
          />
          {dateRange && (
            <Button
              onClick={() => {
                handleTableChange({ currentDateRange: null })
              }}
            >
              Clear
            </Button>
          )}
        </Space>
      </div>
      <Table
        dataSource={partners}
        columns={columns}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="id"
        pagination={{
          showSizeChanger: true,
          total: totalCountOfPartners,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current,
            //@ts-ignore
            currentPageSize: pagination.pageSize,
            //@ts-ignore
            currentOrderDirection: sorter.order,
            //@ts-ignore
            currentOrderBy: sorter.field,
            filters: filters,
          })
        }
      ></Table>
    </CCardBody>
  )
}
